import {ref} from 'vue';
import type {RequestInitLike} from '@prismicio/client';
import type {PrismicDocument} from '@prismicio/types';
import {usePrismicLang} from '#imports';
import {createError, useLogger, usePrismicClient, useSpanWrap, useLocale, useAsyncData} from '#imports';

type FetchOptions = RequestInitLike & {
    priority?: 'low';
};

export function usePrismicDocument() {
    const logger = useLogger();
    const client = usePrismicClient();
    const {locale} = useLocale();
    const {lang} = usePrismicLang();

    function getAsyncDataKey(documentType: string, uid?: string): string {
        return `prismic-document_${documentType}${uid ? `_${uid}` : ''}_${lang.value}`;
    }

    async function getDocument(documentType: string, uid?: string, lowPriority: boolean = false): Promise<PrismicDocument | null> {
        try {
            const fetchOptions = lowPriority ? {...({priority: 'low'} as FetchOptions)} : undefined;
            const getPrismicDocument = () =>
                uid
                    ? client!.getByUID(documentType, uid, {
                          lang: lang.value,
                          fetchOptions,
                      })
                    : client!.getSingle(documentType, {
                          lang: lang.value,
                          fetchOptions,
                      });
            return await useSpanWrap(getPrismicDocument, {httpMethod: 'prismic.get', kind: 'prismic', name: `[prismic] ${documentType}`});
        } catch (err: any) {
            const message = `Could not retrieve ${documentType} document from Prismic for locale ${JSON.stringify(locale)}. Error: name=${
                err.name
            }, message=${err.message}, url=${err.url}, response=${err.response}.`;
            logger.error(message);
            throw createError({message, statusCode: 404});
        }
    }

    async function getDocumentViaAsyncData(documentType: string, uid?: string) {
        const asyncDataKey = getAsyncDataKey(documentType, uid);
        const {data: document} = await useAsyncData(asyncDataKey, () => getDocument(documentType, uid), {
            getCachedData(key, nuxtApp) {
                return nuxtApp.payload.data[key] ?? nuxtApp.static.data[key];
            },
        });

        return document;
    }

    return {
        // functions a-z
        getAsyncDataKey,
        getDocument,
        getDocumentViaAsyncData,
    };
}
